/**
 * Mousai Commerce - cart drawer controller.
 *
 * The ONLY script allowed to mutate the cart or the drawer DOM. All state is
 * server-rendered: every mutation is ONE Cart AJAX request that carries the
 * line change, the gift / protection consequences computed up front, and
 * `sections=cart-drawer`, so the fresh drawer markup comes back in the same
 * response (no second fetch). Only the nodes that changed are swapped in
 * (lines keyed by data-line-key, the bar, the footer). The changed line shows
 * its new quantity at once (optimistic) with a spinner until the server
 * confirms; on any error the drawer re-renders from the server. Mutations are
 * serialized through a single queue so responses can never arrive out of
 * order and overwrite fresher state.
 *
 * 2026-09-16 speed rework (dev-9): was change/add -> GET sections -> gift add
 * -> GET cart.js -> GET sections (up to 5-7 round trips per tap); now 1, plus
 * one reconcile request only when the server state disagrees with the plan.
 */
document.addEventListener('DOMContentLoaded', () => {
  const SECTION_ID = 'cart-drawer';
  const OPT_OUT_KEY = 'tranquira:protection-opt-out';
  const GIFT_OFF_KEY = 'tranquira:gift-off';   // set when the gift was ever not free: gift stays off this session
  const GIFT_TEST_KEY = 'tranquira:gift-test'; // test override, honoured on unpublished themes only
  const FRESH_MS = 60000;                      // a render younger than this is trusted when the drawer opens
  const root = window.Shopify?.routes?.root || '/';
  const designMode = !!window.Shopify?.designMode;
  // ?gift_test=1 on an UNPUBLISHED theme = the session override (phone previews); no effect on the live theme
  try {
    if (new URLSearchParams(window.location.search).get('gift_test') === '1' && window.Shopify?.theme?.role !== 'main') sessionStorage.setItem(GIFT_TEST_KEY, '1');
  } catch (e) {}
  // discount-code UI state survives the footer swap after every render
  const ui = { discountOpen: false, discountValue: '', discountError: '' };
  let lastRenderAt = 0; // performance.now() of the last render from a server response

  const drawer = () => document.getElementById('CartDrawer');
  const overlay = () => document.getElementById('CartDrawerOverlay');
  const content = () => document.getElementById('CartDrawerContent');
  const footData = () => document.getElementById('CartDrawerFooterWrap')?.dataset || {};
  const isOpen = () => drawer()?.classList.contains('is-open');
  const lineEl = (key) => content()?.querySelector(`.cd-item[data-line-key="${CSS.escape(key)}"]`);

  // Session-scoped opt-out: toggling protection off is remembered until the
  // tab session ends; a new session starts ON again.
  const optedOut = () => {
    try { return sessionStorage.getItem(OPT_OUT_KEY) === '1'; } catch { return false; }
  };
  const setOptOut = (on) => {
    try { on ? sessionStorage.setItem(OPT_OUT_KEY, '1') : sessionStorage.removeItem(OPT_OUT_KEY); } catch {}
  };
  const flag = (key) => {
    try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
  };
  const giftOff = () => {
    try { sessionStorage.setItem(GIFT_OFF_KEY, '1'); } catch {}
    document.querySelectorAll('[data-gift-ui]').forEach((el) => { el.hidden = true; });
    document.dispatchEvent(new CustomEvent('tranquira:gift-off'));
  };
  // the gift is live for this session when the theme says on for the
  // shopper's market, or - on an unpublished theme only - the test override
  // is set; never once it was found not free
  const giftActive = (d) => !!d.giftVariantId && !flag(GIFT_OFF_KEY) && (
    d.giftOn === '1' ||
    (d.giftTestable === '1' && flag(GIFT_TEST_KEY) && window.Shopify?.theme?.role !== 'main')
  );

  /* ------------------------------------------------------------- requests */
  // every write asks for the drawer section in the same response
  async function cartRequest(path, body) {
    const res = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...(body || {}), sections: SECTION_ID })
    });
    if (!res.ok) throw new Error(`${path} failed: ${await res.text()}`);
    return res.json();
  }
  const sectionHtml = (json) => (json && json.sections && json.sections[SECTION_ID]) || '';

  async function fetchSection() {
    const res = await fetch(`${root}?sections=${SECTION_ID}`, { cache: 'no-store' });
    if (!res.ok) throw new Error('Section fetch failed');
    const html = (await res.json())[SECTION_ID];
    if (!html) throw new Error('Section not found in response');
    return html;
  }
  // apply the markup that came back with a write; fall back to a fetch when a response carries none
  async function renderFrom(json) {
    const html = sectionHtml(json);
    if (html) applyHtml(html); else applyHtml(await fetchSection());
  }
  async function refresh() { applyHtml(await fetchSection()); }

  /* --------------------------------------------------------------- render */
  // keyed patch of the drawer: title / footer swap only when their markup
  // changed; lines are matched by data-line-key and kept when the server
  // markup is unchanged (no image reload, no lost focus), the bar likewise
  const srvHtml = new WeakMap(); // node -> the server markup it was rendered from
  const place = (parent, node, after) => {
    const want = after ? after.nextElementSibling : parent.firstElementChild;
    if (want !== node) after ? after.after(node) : parent.prepend(node);
  };
  function patchContent(oldC, newC) {
    if (oldC.innerHTML === newC.innerHTML) return;
    const oldList = oldC.querySelector('.cd-items-list');
    const newList = newC.querySelector('.cd-items-list');
    if (!oldList || !newList) { oldC.replaceWith(newC); return; } // empty <-> lines: whole region
    const oldBar = oldC.querySelector('[data-bundle-bar]');
    const newBar = newC.querySelector('[data-bundle-bar]');
    if (newBar && !(oldBar && srvHtml.get(oldBar) === newBar.outerHTML)) {
      srvHtml.set(newBar, newBar.outerHTML);
      oldBar ? oldBar.replaceWith(newBar) : oldList.before(newBar);
    } else if (oldBar && !newBar) oldBar.remove();
    const oldByKey = new Map([...oldList.children].map((el) => [el.dataset.lineKey || '', el]));
    const seen = new Set();
    let cursor = null;
    for (const nl of [...newList.children]) {
      const key = nl.dataset.lineKey || '';
      const ol = key ? oldByKey.get(key) : null;
      let node = nl;
      if (ol && srvHtml.get(ol) === nl.outerHTML) node = ol;
      else { srvHtml.set(nl, nl.outerHTML); if (ol) ol.replaceWith(nl); }
      seen.add(key);
      place(oldList, node, cursor);
      cursor = node;
    }
    oldByKey.forEach((el, key) => { if (!seen.has(key)) el.remove(); });
  }
  const focusHint = () => {
    const a = document.activeElement;
    if (!(a instanceof HTMLElement) || !drawer()?.contains(a)) return null;
    const key = a.closest('[data-line-key]')?.dataset.lineKey;
    if (a.matches('[data-qty-delta]')) return { key, sel: `[data-qty-delta="${a.dataset.qtyDelta}"]` };
    if (a.matches('[data-cart-remove]')) return { key, sel: '[data-cart-remove]' };
    if (a.matches('[data-discount-remove]')) return { sel: `[data-discount-remove="${CSS.escape(a.dataset.discountRemove || '')}"]` };
    for (const sel of ['[data-protection-toggle]', '[data-discount-toggle]', '[data-nudge-toggle]', '[data-nudge-add]', '[data-close-cart]']) if (a.matches(sel)) return { sel };
    if (a.id) return { sel: `#${CSS.escape(a.id)}` };
    return null;
  };
  const restoreFocus = (hint, prev) => {
    if (!hint || (prev && prev.isConnected)) return;
    const scope = hint.key ? lineEl(hint.key) : drawer();
    const el = scope?.querySelector(hint.sel);
    if (el && !el.disabled) el.focus({ preventScroll: true });
  };

  function applyHtml(html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html');
    const hint = focusHint();
    const prevActive = document.activeElement;
    ['CartDrawerTitle', 'CartDrawerFooterWrap'].forEach((id) => {
      const newEl = fresh.getElementById(id);
      const oldEl = document.getElementById(id);
      if (!newEl || !oldEl || srvHtml.get(oldEl) === newEl.outerHTML) return;
      srvHtml.set(newEl, newEl.outerHTML);
      oldEl.replaceWith(newEl);
    });
    const newC = fresh.getElementById('CartDrawerContent');
    const oldC = content();
    if (newC && oldC) patchContent(oldC, newC);
    lastRenderAt = performance.now();

    const count = parseInt(footData().visibleCount || '0', 10);
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    drawer()?.classList.toggle('is-empty', count === 0);
    // the bar's gift wording comes back server-rendered hidden on unpublished themes:
    // the test override reveals it again after every render; the kill switch wins
    if (flag(GIFT_TEST_KEY) && window.Shopify?.theme?.role !== 'main') document.querySelectorAll('#CartDrawer [data-gift-ui][data-gift-testable="1"]').forEach((el) => { el.hidden = false; });
    if (flag(GIFT_OFF_KEY)) document.querySelectorAll('#CartDrawer [data-gift-ui]').forEach((el) => { el.hidden = true; });
    initNudge(false);
    restoreDiscountUi();
    restoreFocus(hint, prevActive);
  }

  /* ------------------------------------------------- gift / protection plan */
  /**
   * Invariants (unchanged from the sequential version, now computed BEFORE
   * the request so the consequences travel in the same cart/update.js):
   *  gift  - present exactly once while the cart holds 3+ units of the bundle
   *          product and the gift is active for this session; removed below 3
   *          units or when not active; never paid for: a gift line whose price
   *          is not 0 is removed and the gift stays off for the session (the
   *          automatic discount is the only thing that makes it free; the
   *          theme never prices it). Re-checked on every render.
   *  protection - on by default when the cart goes from empty to having items
   *          (unless switched off this session), never more than qty 1, never
   *          left behind in an otherwise empty cart.
   * change = { lines: {lineKey: qty}, items: [{quantity, bundle}] } describes
   * the mutation about to be sent; the plan projects the cart after it.
   * Returns { updates: {lineKey: qty}, adds: [{id, quantity}], giftNotFree }.
   */
  function planUpdates(change = {}) {
    const d = footData();
    const lines = [...(content()?.querySelectorAll('.cd-item[data-line-key]:not(.cd-item--gift)') || [])];
    let visible = 0;
    let units = 0;
    for (const el of lines) {
      const key = el.dataset.lineKey;
      const q = change.lines && key in change.lines ? change.lines[key] : (parseInt(el.dataset.lineQty, 10) || 0);
      visible += q;
      if (el.dataset.bundleLine === '1') units += q;
    }
    for (const it of change.items || []) {
      visible += it.quantity;
      if (it.bundle) units += it.quantity;
    }
    const updates = {};
    const adds = [];
    let giftNotFree = false;
    if (d.giftVariantId) {
      const giftQty = parseInt(d.giftQty || '0', 10);
      const linePrice = parseInt(d.giftLinePrice || '0', 10);
      const active = giftActive(d);
      if (giftQty > 1 && d.giftKey) updates[d.giftKey] = 1;
      else if (giftQty > 0 && (units < 3 || !active) && d.giftKey) updates[d.giftKey] = 0;
      else if (giftQty > 0 && linePrice !== 0 && d.giftKey) { updates[d.giftKey] = 0; giftNotFree = true; }
      else if (giftQty === 0 && units >= 3 && active) adds.push({ id: Number(d.giftVariantId), quantity: 1 });
    }
    const pQty = parseInt(d.protectionQty || '0', 10);
    if (pQty > 1 && d.protectionKey) updates[d.protectionKey] = 1;
    else if (pQty > 0 && visible === 0 && d.protectionKey) updates[d.protectionKey] = 0;
    else if (pQty === 0 && visible > 0 && d.protectionDefault === 'true' && d.protectionVariantId && !optedOut()) adds.push({ id: Number(d.protectionVariantId), quantity: 1 });
    return { updates, adds, giftNotFree };
  }
  const noteGift = (plan) => {
    if (plan.giftNotFree) { console.warn(`[gift] gift line was not free (line price ${footData().giftLinePrice}) - removed, gift off for this session`); giftOff(); }
  };
  // after every render: one more request only when the fresh server state
  // still breaks an invariant (e.g. the plan was built on stale markup)
  async function reconcile() {
    for (let i = 0; i < 2; i++) {
      const plan = planUpdates();
      noteGift(plan);
      const updates = { ...plan.updates };
      plan.adds.forEach((a) => { updates[a.id] = a.quantity; }); // cart/update.js adds variants not yet in the cart
      if (!Object.keys(updates).length) return;
      await renderFrom(await cartRequest('cart/update.js', { updates }));
    }
  }

  /* ------------------------------------------------------------ mutations */
  // line quantity changes (+ their gift / protection consequences) in ONE cart/update.js
  async function writeLines(lineUpdates) {
    const plan = planUpdates({ lines: lineUpdates });
    noteGift(plan);
    const updates = { ...plan.updates, ...lineUpdates };
    plan.adds.forEach((a) => { updates[a.id] = a.quantity; });
    await renderFrom(await cartRequest('cart/update.js', { updates }));
    await reconcile();
  }
  // items: [{id, quantity, properties?, selling_plan?}]; opts.handle = the product handle of the
  // items (the bundle product counts towards the gift threshold). The gift and the protection
  // line ride along in the same cart/add.js when the plan calls for them.
  async function addItems(items, opts = {}) {
    const bundle = !!opts.handle && opts.handle === footData().bundleHandle;
    const plan = planUpdates({ items: items.map((it) => ({ quantity: it.quantity, bundle })) });
    noteGift(plan);
    await renderFrom(await cartRequest('cart/add.js', { items: [...items, ...plan.adds] }));
    await reconcile();
  }
  async function setProtection(on) {
    const d = footData();
    setOptOut(!on);
    const plan = planUpdates();
    noteGift(plan);
    const updates = { ...plan.updates };
    plan.adds.forEach((a) => { updates[a.id] = a.quantity; });
    const qty = parseInt(d.protectionQty || '0', 10);
    if (on && qty === 0 && d.protectionVariantId) updates[d.protectionVariantId] = 1;
    if (!on && d.protectionKey) updates[d.protectionKey] = 0;
    if (!Object.keys(updates).length) return;
    await renderFrom(await cartRequest('cart/update.js', { updates }));
    await reconcile();
  }

  // public entry for the PDP (tiers 2-3 post their rows as JSON items): keeps this file the only mutator
  window.TranquiraCart = {
    add: (items, opts) => {
      let failure = null;
      return enqueue(async () => {
        try { await addItems(items, opts); openDrawer(); } catch (err) { failure = err; throw err; }
      }).then(() => { if (failure) throw failure; });
    }
  };

  /* ------------------------------------------------- bundle progress bar */
  // "Add N bras: 3 for the price of 2" bar above the lines while the cart is
  // short of the next round of the offer (server-rendered by the section, so
  // it comes back fresh after every sync); its "Add" button opens the rows. Rows are prefilled here: colours not
  // yet in the cart first, the size of the first bra in the cart, always an
  // available variant; sold-out options cannot be selected. Adding is one
  // POST cart/add.js through the queue - this file stays the only mutator.
  const nudgeEl = () => document.querySelector('#CartDrawerContent [data-nudge]');
  const nudgeData = (el) => {
    try { return JSON.parse(el?.querySelector('[data-nudge-data]')?.textContent || 'null'); } catch { return null; }
  };
  const findVariant = (data, color, size) => data.variants.find((v) =>
    (!data.colorOption || v.color === color) && (!data.sizeOption || v.size === size)) || null;
  const colorHasStock = (data, color) => data.variants.some((v) => v.color === color && v.available);

  function refreshNudgeRow(data, row) {
    const colorSel = row.querySelector('[data-nudge-option="color"]');
    const sizeSel = row.querySelector('[data-nudge-option="size"]');
    const color = colorSel?.value || '';
    if (colorSel) [...colorSel.options].forEach((o) => { o.disabled = !colorHasStock(data, o.value); });
    if (sizeSel) {
      const soldOut = (window.themeStrings?.soldOut || 'Sold out').toLowerCase();
      [...sizeSel.options].forEach((o) => {
        if (!o.value) return;
        const v = findVariant(data, color, o.value);
        const ok = !!(v && v.available);
        o.disabled = !ok;
        o.textContent = ok ? o.value : `${o.value} - ${soldOut}`;
      });
      if (sizeSel.value && sizeSel.selectedOptions[0]?.disabled) sizeSel.value = '';
    }
    const v = findVariant(data, color, sizeSel?.value || '') || data.variants.find((x) => x.color === color) || data.variants[0];
    const price = row.querySelector('[data-nudge-price]');
    if (price) price.textContent = v ? v.price : '';
    const thumb = row.querySelector('[data-nudge-thumb]');
    if (thumb) { if (v?.img) { thumb.src = v.img; thumb.srcset = `${v.img} 1x, ${v.img2} 2x`; thumb.hidden = false; } else { thumb.removeAttribute('src'); thumb.removeAttribute('srcset'); thumb.hidden = true; } }
  }

  // the rows' JSON (every variant + image urls) is parsed only when the rows
  // are opened - not on every render - unless force is set
  function initNudge(force) {
    const el = nudgeEl();
    if (!el || el.dataset.ready === '1') return;
    if (!force && el.querySelector('[data-nudge-rows]')?.hidden !== false) return;
    const data = nudgeData(el);
    if (!data || !Array.isArray(data.variants)) return;
    el.dataset.ready = '1';
    const rows = [...el.querySelectorAll('[data-nudge-row]')];
    const inCart = (data.inCart || []).map((id) => data.variants.find((v) => String(v.id) === String(id))).filter(Boolean);
    const cartColors = new Set(inCart.map((v) => v.color));
    const baseSize = inCart[0]?.size || '';
    const palette = data.colors || [];
    const candidates = [...palette.filter((c) => !cartColors.has(c)), ...palette.filter((c) => cartColors.has(c))];
    let pointer = 0;
    let allSized = true;
    rows.forEach((row) => {
      const colorSel = row.querySelector('[data-nudge-option="color"]');
      const sizeSel = row.querySelector('[data-nudge-option="size"]');
      let picked = null;
      let pickedSize = baseSize;
      // the next candidate colour whose variant in the base size is available
      for (let k = 0; k < candidates.length && !picked; k++) {
        const c = candidates[(pointer + k) % candidates.length];
        const v = findVariant(data, c, baseSize);
        if (v && v.available) { picked = c; pointer = (pointer + k + 1) % candidates.length; }
      }
      if (!picked) {
        // no colour has the base size in stock: first colour with any stock, size left to the shopper
        picked = candidates.find((c) => colorHasStock(data, c)) || candidates[0] || '';
        pickedSize = '';
        allSized = false;
        if (candidates.length) pointer = (candidates.indexOf(picked) + 1) % candidates.length;
      }
      if (colorSel && picked) colorSel.value = picked;
      if (sizeSel) sizeSel.value = pickedSize;
      refreshNudgeRow(data, row);
    });
    const hint = el.querySelector('[data-nudge-hint]');
    if (hint) hint.textContent = baseSize && allSized ? (hint.dataset.withSize || '').split('%size%').join(baseSize) : (hint.dataset.noSize || '');
  }

  function nudgeAdd(el) {
    const data = nudgeData(el);
    if (!data) return;
    const error = el.querySelector('[data-nudge-error]');
    const items = new Map();
    let invalid = false;
    el.querySelectorAll('[data-nudge-row]').forEach((row) => {
      const color = row.querySelector('[data-nudge-option="color"]')?.value || '';
      const size = row.querySelector('[data-nudge-option="size"]')?.value || '';
      const v = findVariant(data, color, size);
      if ((data.sizeOption && !size) || !v || !v.available) { invalid = true; row.classList.add('is-invalid'); return; }
      items.set(v.id, (items.get(v.id) || 0) + 1);
    });
    if (invalid) {
      if (error) { error.textContent = error.dataset.sizeRequired || error.dataset.generic || ''; error.hidden = false; }
      return;
    }
    if (error) error.hidden = true;
    const btn = el.querySelector('[data-nudge-add]');
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    enqueue(async () => {
      try {
        await addItems([...items].map(([id, quantity]) => ({ id, quantity })), { handle: footData().bundleHandle });
      } catch (err) {
        console.error('[cart-drawer] nudge add failed', err);
        await refresh().catch(() => {});
        const e2 = nudgeEl()?.querySelector('[data-nudge-error]');
        if (e2) { e2.textContent = e2.dataset.generic || ''; e2.hidden = false; }
        const b = nudgeEl()?.querySelector('[data-nudge-add]');
        if (b) { b.disabled = false; b.classList.remove('is-loading'); }
      }
    });
  }

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-nudge-toggle]');
    if (toggle) {
      e.preventDefault();
      const rows = toggle.closest('[data-nudge]')?.querySelector('[data-nudge-rows]');
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? (toggle.dataset.labelClose || '') : (toggle.dataset.labelOpen || '');
      if (rows) rows.hidden = !open;
      if (open) { initNudge(true); rows?.querySelector('select')?.focus({ preventScroll: true }); }
      return;
    }
    const add = e.target.closest('[data-nudge-add]');
    if (add) { e.preventDefault(); nudgeAdd(add.closest('[data-nudge]')); }
  });
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-nudge-option]');
    if (!sel) return;
    const el = sel.closest('[data-nudge]');
    const row = sel.closest('[data-nudge-row]');
    const data = nudgeData(el);
    if (!data || !row) return;
    refreshNudgeRow(data, row);
    row.classList.remove('is-invalid');
    const error = el.querySelector('[data-nudge-error]');
    if (error) error.hidden = true;
  });

  /* ------------------------------------------------------- discount code */
  // Cart API: cart/update.js {discount: "A,B"} stores every code; the
  // response's discount_codes[] says which ones apply. A code that does not
  // apply to this cart is stripped again right away and the invalid message
  // shows, so the cart never carries a dead code into checkout. '' removes
  // all. The codes already applied are read from the footer chips (rendered
  // from cart.discount_applications), so no cart.js round trip is needed.
  const codeBox = () => document.querySelector('#CartDrawer [data-discount]'); // the block lives in the pinned footer
  const appliedCodes = () => [...document.querySelectorAll('#CartDrawer [data-discount-remove]')].map((b) => b.dataset.discountRemove).filter(Boolean);

  function restoreDiscountUi() {
    const box = codeBox();
    if (!box) return;
    const form = box.querySelector('[data-discount-form]');
    const toggle = box.querySelector('[data-discount-toggle]');
    const error = box.querySelector('[data-discount-error]');
    if (form) form.hidden = !ui.discountOpen;
    toggle?.setAttribute('aria-expanded', String(ui.discountOpen));
    const input = form?.querySelector('input');
    if (input) input.value = ui.discountValue;
    if (error) { error.textContent = ui.discountError; error.hidden = !ui.discountError; }
    box.querySelector('.cd-code__apply')?.classList.remove('is-loading');
  }

  async function applyCode(code) {
    const existing = appliedCodes();
    if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) return true;
    const after = await cartRequest('cart/update.js', { discount: [...existing, code].join(',') });
    const entry = (after.discount_codes || []).find((c) => c.code.toLowerCase() === code.toLowerCase());
    if (entry && entry.applicable) { await renderFrom(after); await reconcile(); return true; }
    // not applicable here (or unknown): put the cart back to the codes that did apply
    await renderFrom(await cartRequest('cart/update.js', { discount: existing.join(',') }));
    await reconcile();
    return false;
  }

  async function removeCode(code) {
    const remaining = appliedCodes().filter((c) => c.toLowerCase() !== code.toLowerCase());
    await renderFrom(await cartRequest('cart/update.js', { discount: remaining.join(',') }));
    await reconcile();
  }

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-discount-toggle]');
    if (toggle) {
      e.preventDefault();
      ui.discountOpen = toggle.getAttribute('aria-expanded') !== 'true';
      if (!ui.discountOpen) { ui.discountError = ''; }
      restoreDiscountUi();
      if (ui.discountOpen) codeBox()?.querySelector('input')?.focus({ preventScroll: true });
      return;
    }
    const remove = e.target.closest('[data-discount-remove]');
    if (remove) {
      e.preventDefault();
      const code = remove.dataset.discountRemove;
      remove.closest('.cd-code__chip')?.classList.add('is-removing');
      enqueue(() => removeCode(code));
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input');
    const code = (input?.value || '').trim();
    if (!code) { input?.focus(); return; }
    const box = form.closest('[data-discount]');
    ui.discountValue = code;
    ui.discountError = '';
    form.querySelector('.cd-code__apply')?.classList.add('is-loading');
    enqueue(async () => {
      let ok = false;
      try {
        ok = await applyCode(code);
      } catch (err) {
        console.error('[cart-drawer] discount apply failed', err);
        ui.discountError = box?.dataset.msgError || '';
        await refresh();
        return;
      }
      if (ok) { ui.discountOpen = false; ui.discountValue = ''; ui.discountError = ''; }
      else { ui.discountError = box?.dataset.msgInvalid || ''; }
      restoreDiscountUi();
      if (!ok) codeBox()?.querySelector('input')?.focus({ preventScroll: true });
    });
  });

  /* ---------------------------------------------------- serialized mutations */
  // is-busy = a cart write is in flight (totals dim, checkout waits);
  // is-syncing = a background refresh (nothing dims)
  let chain = Promise.resolve();
  function enqueue(op, opts = {}) {
    const cls = opts.silent ? 'is-syncing' : 'is-busy';
    const run = chain.catch(() => {}).then(async () => {
      drawer()?.classList.add(cls);
      try {
        await op();
      } catch (err) {
        console.error('[cart-drawer]', err);
        pendingQty.clear();
        await refresh().catch(() => {});
        clearOptimistic();
      } finally {
        drawer()?.classList.remove(cls);
      }
    });
    chain = run;
    return run;
  }
  const settled = () => chain.catch(() => {});

  /* ---------------------------------------------------------- open / close */
  let lastTrigger = null;

  const openDrawer = () => {
    const el = drawer();
    if (!el || isOpen()) return;
    lastTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    el.classList.add('is-open');
    overlay()?.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    el.querySelector('[data-close-cart]')?.focus({ preventScroll: true });
  };

  const closeDrawer = () => {
    if (!isOpen()) return;
    drawer()?.classList.remove('is-open');
    overlay()?.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    lastTrigger?.focus({ preventScroll: true });
    lastTrigger = null;
  };

  // background refresh (no dimming): the drawer already shows the server
  // state it was delivered with, or the state of the last write; a refresh is
  // only worth a request when that render is older than FRESH_MS
  const refreshIfStale = () => {
    if (designMode || performance.now() - lastRenderAt < FRESH_MS) return;
    enqueue(async () => { await refresh(); await reconcile(); }, { silent: true });
  };

  /* --------------------------------------------------------- add to cart */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    // a product form without a variant id (size not chosen, or a multi-piece tier whose rows are added
    // as JSON by pdp.js) must never reach cart/add.js as a raw post - it would add the wrong thing
    const idField = form.querySelector('[data-variant-id], input[name="id"]');
    if (idField && !idField.value) { console.warn('[cart-drawer] product form submitted without a variant id - ignored'); return; }

    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;

    // the form's fields as one JSON item so the gift / protection lines can ride along
    const fd = new FormData(form);
    const item = { id: Number(fd.get('id')), quantity: parseInt(fd.get('quantity'), 10) || 1 };
    const properties = {};
    for (const [k, v] of fd.entries()) { const m = /^properties\[(.+)\]$/.exec(k); if (m) properties[m[1]] = v; }
    if (Object.keys(properties).length) item.properties = properties;
    if (fd.get('selling_plan')) item.selling_plan = Number(fd.get('selling_plan'));

    enqueue(async () => {
      await addItems([item], { handle: form.dataset.productHandle });
      openDrawer();
    }).finally(() => {
      if (btn) btn.disabled = false;
    });
  });

  // checkout never leaves with a write still in flight: wait for the queue, then submit for real
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'CartDrawerCheckoutForm') return;
    if (!drawer()?.classList.contains('is-busy')) return;
    e.preventDefault();
    const btn = form.querySelector('[name="checkout"]');
    if (btn) btn.disabled = true;
    settled().then(() => { if (btn) btn.disabled = false; form.requestSubmit(btn || undefined); });
  });

  /* ------------------------------------------------------ click delegation */
  // optimistic line quantity: the number changes at once, the line shows a
  // spinner, one request carries the LATEST target for that line (rapid taps
  // coalesce into a single write)
  const pendingQty = new Map(); // line key -> target quantity
  // back to the server state after a failed write: numbers and spinners reset on every line
  const clearOptimistic = () => {
    content()?.querySelectorAll('.cd-item[data-line-key]').forEach((line) => {
      line.classList.remove('is-updating', 'is-removing');
      const num = line.querySelector('.cd-qty-num');
      if (num && line.dataset.lineQty) num.textContent = line.dataset.lineQty;
    });
    drawer()?.querySelector('.cd-sp-row')?.classList.remove('is-updating');
  };
  const showQty = (line, q) => {
    const num = line.querySelector('.cd-qty-num');
    if (num) num.textContent = q;
    line.classList.add('is-updating');
    line.classList.toggle('is-removing', q === 0);
  };
  function queueQty(key, target) {
    const line = lineEl(key);
    if (!line) return;
    pendingQty.set(key, target);
    showQty(line, target);
    enqueue(async () => {
      if (!pendingQty.has(key)) return; // a later tap on this line already carried the write
      const q = pendingQty.get(key);
      pendingQty.delete(key);
      const l = lineEl(key);
      const server = l ? parseInt(l.dataset.lineQty, 10) || 0 : null;
      if (l && q === server) { showQty(l, server); l.classList.remove('is-updating', 'is-removing'); return; }
      await writeLines({ [key]: q });
    });
  }

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      queueQty(remove.dataset.cartRemove, 0);
      return;
    }

    const qty = e.target.closest('[data-cart-qty]');
    if (qty) {
      e.preventDefault();
      const key = qty.dataset.cartQty;
      const line = lineEl(key);
      const delta = parseInt(qty.dataset.qtyDelta, 10);
      const current = pendingQty.has(key) ? pendingQty.get(key) : (parseInt(line?.dataset.lineQty, 10) || 0);
      const target = Number.isFinite(delta) ? Math.max(0, current + delta) : Math.max(0, parseInt(qty.dataset.qty, 10) || 0);
      queueQty(key, target);
      return;
    }

    if (e.target.closest('[data-clear-cart]')) {
      e.preventDefault();
      enqueue(async () => { await renderFrom(await cartRequest('cart/clear.js')); });
      return;
    }

    if (e.target.closest('[data-open-cart]')) {
      e.preventDefault();
      // open at once with the state on the page (server-rendered at load, or
      // the last write's response); a background refresh follows only when
      // that render is stale. Design mode: the editor preview runs on the
      // myshopify domain, where the ?sections= fetch 301s to the primary
      // domain and dies cross-origin - open without any fetch.
      openDrawer();
      refreshIfStale();
      return;
    }

    if (e.target.closest('[data-close-cart]')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  /* -------------------------------------------------- protection toggle */
  // the switch flips at once; the write goes out with the state the switch
  // has when its turn in the queue comes (rapid flips coalesce)
  document.addEventListener('change', (e) => {
    const toggle = e.target.closest('[data-protection-toggle]');
    if (!toggle) return;
    toggle.closest('.cd-sp-row')?.classList.add('is-updating');
    enqueue(async () => {
      const t = document.querySelector('#CartDrawer [data-protection-toggle]');
      if (!t) return;
      const on = t.checked;
      const has = parseInt(footData().protectionQty || '0', 10) > 0;
      if (on === has) { setOptOut(!on); t.closest('.cd-sp-row')?.classList.remove('is-updating'); return; }
      await setProtection(on);
    });
  });

  // gift copy anywhere on the page (buy box line, 2+1 ribbon, sticky bar, announcement slide,
  // homepage line, drawer): hidden for the session by the kill switch; on unpublished themes the
  // test override reveals the markup the theme rendered hidden. The live theme never renders
  // that hidden markup, so nothing can be switched on there.
  (() => {
    const off = flag(GIFT_OFF_KEY);
    const test = flag(GIFT_TEST_KEY) && window.Shopify?.theme?.role !== 'main';
    let revealed = false;
    document.querySelectorAll('[data-gift-ui]').forEach((el) => {
      if (off) { el.hidden = true; return; }
      if (el.hidden && el.dataset.giftTestable === '1' && test) { el.hidden = false; revealed = true; }
    });
    if (revealed) document.dispatchEvent(new CustomEvent('tranquira:gift-reveal'));
  })();

  // the page's own render is server markup too: remember it so the first patch keeps untouched nodes
  drawer()?.querySelectorAll('#CartDrawerTitle, #CartDrawerFooterWrap, [data-bundle-bar], .cd-items-list > *').forEach((el) => srvHtml.set(el, el.outerHTML));

  /* --------------------------------------------- prefetch / staleness */
  // the drawer arrives server-rendered with the page; one idle-time refresh
  // after load catches a cart changed in another tab before the first open,
  // and a page restored from the back-forward cache refreshes again
  if (!designMode && drawer()) {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    const afterLoad = () => idle(() => { if (document.visibilityState === 'visible') refreshIfStale(); }, { timeout: 4000 });
    document.readyState === 'complete' ? afterLoad() : window.addEventListener('load', afterLoad, { once: true });
    window.addEventListener('pageshow', (e) => { if (e.persisted) { lastRenderAt = 0; refreshIfStale(); } });
  }

  /* ---------------------------------------------- deep link (?cart=open) */
  // /cart redirects here with ?cart=open when the cart has items; open the
  // drawer and strip the param so refresh/share doesn't re-trigger it.
  const params = new URLSearchParams(window.location.search);
  if (params.get('cart') === 'open') {
    params.delete('cart');
    const query = params.toString();
    history.replaceState(
      null,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash
    );
    // Double rAF: paint one frame in the closed state so the slide-in plays.
    requestAnimationFrame(() => requestAnimationFrame(openDrawer));
  }

  /* ------------------------------------------------- theme editor support */
  // Design mode only: the drawer is off-canvas and invisible, so the editor
  // needs a way to show it. Selecting the Cart drawer section force-opens it
  // (no cart mutations - the server-rendered state is already correct),
  // deselecting closes it, and a settings change re-renders the section
  // (shopify:section:load replaces the DOM, dropping is-open), so the fresh
  // markup is re-opened to keep live edits visible. Storefront sessions never
  // attach these listeners.
  if (designMode) {
    const ownsDrawer = (e) => !!e.target?.querySelector?.('#CartDrawer');
    document.addEventListener('shopify:section:select', (e) => {
      if (ownsDrawer(e)) openDrawer();
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      if (ownsDrawer(e)) closeDrawer();
    });
    document.addEventListener('shopify:section:load', (e) => {
      if (ownsDrawer(e)) openDrawer();
    });
  }

  /* ------------------------------------------------- keyboard: esc + trap */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (e.key !== 'Tab' || !isOpen()) return;

    const el = drawer();
    const focusables = [...el.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )].filter((f) => !f.disabled && f.getClientRects().length > 0);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const inside = el.contains(document.activeElement);

    if (e.shiftKey && (document.activeElement === first || !inside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
      e.preventDefault();
      first.focus();
    }
  });
});
